import {Injectable} from "@angular/core";
import {AuthorizationServiceProvider} from "../../auth_profile/services/authorization-service.provider";
import {AuthorizedUserDetailsService, UserRole} from "../../auth_profile/services/api/authorization.api";
import {forkJoin, map, Observable, of, ReplaySubject, switchMap} from "rxjs";
import {NativeServiceApiProvider} from "../../services/native-api-provider.service";
import {CasaNativeApiV3} from "../../auth_profile/services/api/casa-native-api";
import {V3School} from "../../auth_profile/model/v3/schools";

export enum AccessRequired {
  Video = "area.video"
}

export class AccessList {
  constructor(public videoDisabled: boolean = true) {
  }

  public disabledList(): Array<string> {
    const result: Array<string> = [];
    if (this.videoDisabled) result.push("video");
    return result;
  }
}

@Injectable({
  providedIn: 'root'
})
export class FunctionalAccessService {
  private accessStateSubject = new ReplaySubject<AccessList>(1)

  public getAccessState(): Observable<AccessList> {
    return this.accessStateSubject.asObservable();
  }

  constructor(
    authServiceProvider: AuthorizationServiceProvider,
    private provider: NativeServiceApiProvider,
  ) {
    authServiceProvider.getCommonRoutines().subscribe(
      authApi => {
        if (authApi.isNativeAuthorization()) {
          // fill with default values (everything is available)
          this.setAsDisabled(new Set<string>());
          return;
        }
        // whole web service will be reinitialized after login
        // I don't have to trace the authorization then
        if (!authApi.isTokenValid()) return;

        forkJoin([
          authServiceProvider.getAuthDetailsService(), this.provider.casa()
        ]).pipe(
          switchMap(([detailsApi, provider]) => detailsApi.getUserRole().pipe(
            switchMap<UserRole, Observable<V3School>>(role => this.getSchoolByRole(detailsApi, provider, role)),
            map<V3School, Set<string>>(school => this.mapSchoolToDisabledFunctionalities(school))
          ))
        ).subscribe(
          toDisable => this.setAsDisabled(toDisable)
        );
      }
    )
  }

  private mapSchoolToDisabledFunctionalities(school: V3School): Set<string> {
    const paramExpr = new RegExp("^\\s*ui_disable\\s*=\\s*(.*)");
    const splitExpr = new RegExp("\\s*;\\s*");

    if (!school || !school.labels) return new Set<string>();
    const functionalitiesLabel = school.labels.split(",").find(it =>
      it.match(paramExpr)
    )
    if (!functionalitiesLabel) return new Set<string>();

    const functionalitiesList = paramExpr.exec(functionalitiesLabel)[1].split(splitExpr);
    return new Set<string>(functionalitiesList.map(it => it.trim()));
  }

  private getSchoolByRole(authApi: AuthorizedUserDetailsService, provider: CasaNativeApiV3, role: UserRole): Observable<V3School> {
    if (role == UserRole.Student || role == UserRole.Teacher) return authApi.getSelfSchoolId().pipe(
      switchMap(schoolId => provider.getSchool(schoolId))
    )
    else return of(null)
  }

  private setAsDisabled(toDisable: Set<string>) {
    const result = new AccessList(
      toDisable.has(AccessRequired.Video)
    )
    this.accessStateSubject.next(result);
  }
}
